import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, CalendarHeart, XCircle } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const PaymentSuccess = () => {
  const { t, to } = useLang();
  const s = t.paymentStatus;
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let attempts = 0;
    let timer;
    const poll = async () => {
      try {
        const res = await fetch(`${API}/payments/status/${sessionId}`);
        if (!res.ok) throw new Error();
        const data = await res.json();
        if (data.payment_status === "paid") {
          setStatus("paid");
          return;
        }
      } catch (e) {
        /* keep polling */
      }
      attempts += 1;
      if (attempts < 10) {
        timer = setTimeout(poll, 2000);
      } else {
        setStatus("pending");
      }
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <section data-testid="payment-success-page" className="pt-40 pb-28 max-w-2xl mx-auto px-4 sm:px-8 text-center">
      {status === "loading" && (
        <>
          <Loader2 className="w-12 h-12 text-terracotta animate-spin mx-auto mb-6" />
          <h1 className="font-serif text-3xl sm:text-4xl font-semibold">{s.confirmingTitle}</h1>
          <p className="mt-4 text-clay">{s.confirmingText}</p>
        </>
      )}
      {status === "paid" && (
        <>
          <span className="w-20 h-20 rounded-full bg-sage-soft flex items-center justify-center mx-auto mb-8">
            <CheckCircle2 className="w-10 h-10 text-sage-dark" />
          </span>
          <h1 className="font-serif text-4xl sm:text-5xl font-semibold text-balance" data-testid="payment-confirmed-heading">
            {s.confirmedTitle}
          </h1>
          <p className="mt-5 text-base sm:text-lg text-clay leading-relaxed">{s.confirmedText}</p>
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to={to("/booking?type=coaching")}
              data-testid="payment-success-book-session-btn"
              className="inline-flex items-center gap-2.5 rounded-full bg-terracotta text-linen px-8 py-4 text-base font-semibold transition-[background-color,transform] duration-200 hover:bg-terracotta-dark [@media(hover:hover)]:hover:-translate-y-0.5"
            >
              <CalendarHeart className="w-5 h-5" />
              {s.bookSession}
            </Link>
            <Link to={to("/")} data-testid="payment-success-home-btn" className="text-sm font-semibold text-taupe hover:text-espresso transition-colors">
              {s.backHome}
            </Link>
          </div>
        </>
      )}
      {(status === "pending" || status === "error") && (
        <>
          <h1 className="font-serif text-3xl sm:text-4xl font-semibold">{s.pendingTitle}</h1>
          <p className="mt-4 text-clay leading-relaxed">
            {s.pendingPre}{" "}
            <a href="mailto:info@natalialifecoach.com" className="font-semibold text-terracotta">
              info@natalialifecoach.com
            </a>
            {s.pendingPost}
          </p>
          <Link
            to={to("/")}
            data-testid="payment-pending-home-btn"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-cream border border-border px-7 py-3.5 text-sm font-semibold hover:bg-sand transition-colors"
          >
            {s.backHome}
          </Link>
        </>
      )}
    </section>
  );
};

export const PaymentCancel = () => {
  const { t, to } = useLang();
  const s = t.paymentStatus;
  return (
    <section data-testid="payment-cancel-page" className="pt-40 pb-28 max-w-2xl mx-auto px-4 sm:px-8 text-center">
      <span className="w-20 h-20 rounded-full bg-cream flex items-center justify-center mx-auto mb-8">
        <XCircle className="w-10 h-10 text-taupe" />
      </span>
      <h1 className="font-serif text-4xl sm:text-5xl font-semibold text-balance">{s.cancelTitle}</h1>
      <p className="mt-5 text-base sm:text-lg text-clay leading-relaxed">{s.cancelText}</p>
      <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
        <Link
          to={to("/payments")}
          data-testid="payment-cancel-retry-btn"
          className="inline-flex items-center gap-2 rounded-full bg-terracotta text-linen px-8 py-4 text-base font-semibold transition-[background-color,transform] duration-200 hover:bg-terracotta-dark [@media(hover:hover)]:hover:-translate-y-0.5"
        >
          {s.cancelRetry}
        </Link>
        <Link to={to("/booking")} data-testid="payment-cancel-book-btn" className="text-sm font-semibold text-taupe hover:text-espresso transition-colors">
          {s.cancelAlt}
        </Link>
      </div>
    </section>
  );
};
